import React, { useState, useEffect } from 'react';
import { X, Save, Loader2 } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001/api';

const FORM_VACIO = {
  documento_identidad: '',
  nombre_completo: '',
  email: '',
  telefono: '',
  cargo: '',
  departamento: '',
  fecha_ingreso: '',
  es_lider: false,
  lider_id: '',
  is_active: true,
};

const PersonalFormModal = ({ isOpen, onClose, personalId, onSaved }) => {
  const [formData, setFormData] = useState(FORM_VACIO);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingPersona, setLoadingPersona] = useState(false);
  const [lideres, setLideres] = useState([]);

  const token = () => localStorage.getItem('dashboardToken');

  // Cargar líderes disponibles (para el select, incluye líderes que a su
  // vez reportan a otro líder -- jerarquías de varios niveles son válidas)
  useEffect(() => {
    if (!isOpen) return;
    const cargarLideres = async () => {
      try {
        const response = await axios.get(`${API_URL}/personal/lideres`, {
          headers: { Authorization: `Bearer ${token()}` },
          params: { activos_only: true },
        });
        setLideres(response.data || []);
      } catch (error) {
        // El select simplemente queda vacío si falla
      }
    };
    cargarLideres();
  }, [isOpen]);

  // Cargar la persona a editar (o limpiar el formulario para una nueva)
  useEffect(() => {
    if (!isOpen) return;
    setErrors({});

    if (!personalId) {
      setFormData(FORM_VACIO);
      return;
    }

    const cargarPersona = async () => {
      setLoadingPersona(true);
      try {
        const response = await axios.get(`${API_URL}/personal/${personalId}`, {
          headers: { Authorization: `Bearer ${token()}` },
        });
        const p = response.data;
        setFormData({
          documento_identidad: p.documento_identidad || '',
          nombre_completo: p.nombre_completo || '',
          email: p.email || '',
          telefono: p.telefono || '',
          cargo: p.cargo || '',
          departamento: p.departamento || '',
          fecha_ingreso: p.fecha_ingreso || '',
          es_lider: !!p.es_lider,
          lider_id: p.lider_id || '',
          is_active: p.is_active !== undefined ? p.is_active : true,
        });
      } catch (error) {
        setErrors({ submit: 'No se pudo cargar la información de esta persona.' });
      } finally {
        setLoadingPersona(false);
      }
    };
    cargarPersona();
  }, [personalId, isOpen]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.documento_identidad.trim()) {
      newErrors.documento_identidad = 'El documento de identidad es requerido';
    }
    if (!formData.nombre_completo.trim()) {
      newErrors.nombre_completo = 'El nombre completo es requerido';
    }
    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'El email no tiene un formato válido';
    }
    if (!formData.cargo.trim()) {
      newErrors.cargo = 'El cargo es requerido';
    }
    if (!formData.es_lider && !formData.lider_id) {
      newErrors.lider_id = 'Debes asignar un líder (o marcar a esta persona como líder)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      const payload = {
        documento_identidad: formData.documento_identidad.trim(),
        nombre_completo: formData.nombre_completo.trim(),
        email: formData.email.trim() || null,
        telefono: formData.telefono.trim() || null,
        cargo: formData.cargo.trim(),
        departamento: formData.departamento.trim() || null,
        fecha_ingreso: formData.fecha_ingreso || null,
        es_lider: formData.es_lider,
        lider_id: formData.lider_id || null,
      };

      if (personalId) {
        payload.is_active = formData.is_active;
        await axios.put(`${API_URL}/personal/${personalId}`, payload, {
          headers: { Authorization: `Bearer ${token()}` },
        });
      } else {
        await axios.post(`${API_URL}/personal/`, payload, {
          headers: { Authorization: `Bearer ${token()}` },
        });
      }

      onSaved();
      onClose();
    } catch (error) {
      setErrors({
        submit: error.response?.data?.detail || 'Error al guardar. Intenta nuevamente.',
      });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={onClose}></div>

        <div className="relative w-full max-w-lg transform overflow-hidden rounded-lg bg-white px-6 py-6 shadow-xl">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">
              {personalId ? 'Editar Persona' : 'Agregar Persona'}
            </h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X className="h-6 w-6" />
            </button>
          </div>

          {loadingPersona ? (
            <div className="py-8 text-center text-gray-500">Cargando...</div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Documento de Identidad *
                  </label>
                  <input
                    type="text"
                    name="documento_identidad"
                    value={formData.documento_identidad}
                    onChange={handleInputChange}
                    className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm ${
                      errors.documento_identidad ? 'border-red-300' : ''
                    }`}
                    placeholder="Ej: 1234567890"
                  />
                  {errors.documento_identidad && (
                    <p className="mt-1 text-xs text-red-600">{errors.documento_identidad}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Nombre Completo *</label>
                  <input
                    type="text"
                    name="nombre_completo"
                    value={formData.nombre_completo}
                    onChange={handleInputChange}
                    className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm ${
                      errors.nombre_completo ? 'border-red-300' : ''
                    }`}
                    placeholder="Ej: Juan Pérez"
                  />
                  {errors.nombre_completo && (
                    <p className="mt-1 text-xs text-red-600">{errors.nombre_completo}</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm ${
                      errors.email ? 'border-red-300' : ''
                    }`}
                    placeholder="Ej: juan.perez@aossas.com"
                  />
                  {errors.email && <p className="mt-1 text-xs text-red-600">{errors.email}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Teléfono</label>
                  <input
                    type="text"
                    name="telefono"
                    value={formData.telefono}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    placeholder="Ej: 3001234567"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Cargo *</label>
                  <input
                    type="text"
                    name="cargo"
                    value={formData.cargo}
                    onChange={handleInputChange}
                    className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm ${
                      errors.cargo ? 'border-red-300' : ''
                    }`}
                    placeholder="Ej: Desarrollador Backend"
                  />
                  {errors.cargo && <p className="mt-1 text-xs text-red-600">{errors.cargo}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Departamento</label>
                  <input
                    type="text"
                    name="departamento"
                    value={formData.departamento}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    placeholder="Ej: Operaciones"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Fecha de Ingreso</label>
                <input
                  type="date"
                  name="fecha_ingreso"
                  value={formData.fecha_ingreso}
                  onChange={handleInputChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="es_lider"
                  name="es_lider"
                  checked={formData.es_lider}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <label htmlFor="es_lider" className="ml-2 block text-sm text-gray-900">
                  Esta persona es líder (tiene gente a cargo)
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Líder {!formData.es_lider && '*'} ({lideres.length} disponibles)
                </label>
                <select
                  name="lider_id"
                  value={formData.lider_id}
                  onChange={handleInputChange}
                  className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm ${
                    errors.lider_id ? 'border-red-300' : ''
                  }`}
                >
                  <option value="">
                    {formData.es_lider ? 'Sin jefe (líder de nivel superior)' : 'Seleccionar líder'}
                  </option>
                  {lideres
                    .filter((l) => l.id !== personalId)
                    .map((l) => (
                      <option key={l.id} value={l.id}>
                        {l.nombre_completo}
                      </option>
                    ))}
                </select>
                {errors.lider_id && <p className="mt-1 text-xs text-red-600">{errors.lider_id}</p>}
                <p className="mt-1 text-xs text-gray-500">
                  Un líder también puede tener su propio jefe (jerarquías de varios niveles).
                </p>
              </div>

              {personalId && (
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="is_active"
                    name="is_active"
                    checked={formData.is_active}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <label htmlFor="is_active" className="ml-2 block text-sm text-gray-900">
                    Persona activa
                  </label>
                </div>
              )}

              {errors.submit && (
                <div className="rounded-md bg-red-50 p-4">
                  <p className="text-sm text-red-800">{errors.submit}</p>
                </div>
              )}

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={onClose}
                  className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                  disabled={loading}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="inline-flex justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Guardando...
                    </>
                  ) : (
                    <>
                      <Save className="mr-2 h-4 w-4" />
                      {personalId ? 'Actualizar' : 'Crear'}
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default PersonalFormModal;
